import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '../../services/api';
import { useTasksQuery, useProjectsQuery, useSettingsQuery, useCustomFieldsQuery, useMyBotsQuery } from '../../hooks/queries';
import { taskKeys, projectKeys } from '../../lib/queryKeys';
import { useAuth } from '../../contexts/AuthContext';
import { useDateTime } from '../../contexts/DateTimeContext';
import { Button } from '../../components/ui/button';
import TasksTable from '../../components/TasksTable';
import Segmented from '../../components/ui/segmented';
import ColumnSelector from '../../components/ColumnSelector';
import { PageHeaderSkeleton, TaskListSkeleton } from '../../components/skeletons/PageSkeletons';
import { useTask } from '../../contexts/TaskContext';
import { isMyWork, isDelegatedByUser } from '../../lib/taskFilters';
import HomeDayCalendar from '../../components/home/HomeDayCalendar';
import HomeNeedsYou from '../../components/home/HomeNeedsYou';
import { greetingText, todayText } from '../../components/home/greeting';
import QuickInput from '../../components/create/QuickInput';
import BotAvatar from '../../components/bots/BotAvatar';
import { useShellActions } from '../../components/layout/shellActions';
import { useMediaQuery, SCREEN } from '../../hooks/useMediaQuery';
import {
  Calendar, ChevronRight, Mic, Plus, Search, CalendarDays, Settings, CalendarCheck,
} from 'lucide-react';
import { EMPTY_ARRAY } from '../../lib/empty';

// Column definitions (same as TasksTable); labelKey is translated at render
const COLUMN_DEFINITIONS = [
  { key: 'title',      labelKey: 'tasks.task_column',  required: true,  span: 5, defaultVisible: true  },
  { key: 'status',     labelKey: 'tasks.status',       required: false, span: 2, defaultVisible: true  },
  { key: 'priority',   labelKey: 'tasks.priority',     required: false, span: 2, defaultVisible: true  },
  // Off by default: today's list is today (the overdue list always shows it).
  { key: 'due_date',   labelKey: 'tasks.due_date',     required: false, span: 2, defaultVisible: false },
  { key: 'duration',   labelKey: 'tasks.duration',     required: false, span: 1, defaultVisible: true  },
  { key: 'project',    labelKey: 'tasks.project',      required: false, span: 2, defaultVisible: false },
  { key: 'labels',     labelKey: 'tasks.labels',       required: false, span: 2, defaultVisible: false },
  { key: 'scheduled',  labelKey: 'tasks.scheduled',    required: false, span: 2, defaultVisible: false },
  { key: 'task_type',  labelKey: 'tasks.type',         required: false, span: 2, defaultVisible: false },
  { key: 'assignee',   labelKey: 'tasks.assignee',     required: false, span: 2, defaultVisible: true },
  { key: 'created_at', labelKey: 'tasks.date_created', required: false, span: 2, defaultVisible: false },
];

/** A card of Home (Overdue, Today, a next day): its title, count and date, folding to that
 * header; the tasks under it. */
const FoldCard = ({ id, title, count, countClass = 'text-muted-foreground', meta, open, onToggle, children }) => {
  const testId = id === 'overdue' || id === 'today' ? `home-${id}` : 'home-day';
  return (
    <section className="rounded-xl border border-border bg-accent/60 min-w-0" data-testid={`${testId}-card`}>
      <button type="button" onClick={() => onToggle(id)} aria-expanded={open} data-testid={testId}
        className={`w-full flex items-center gap-2 px-4 py-3 text-left ${open ? 'border-b border-border' : ''}`}>
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground min-w-0">
          <span className="truncate">{title}</span>
          <span className={`font-normal tabular-nums ${countClass}`}>{count}</span>
        </h2>
        {meta && <span className="text-xs text-muted-foreground truncate">{meta}</span>}
        <ChevronRight className={`ml-auto w-4 h-4 flex-shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="overflow-x-auto">{children}</div>}
    </section>
  );
};

const EMPTY = Object.freeze([]);

const AgendaPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tasksQuery = useTasksQuery();
  const projectsQuery = useProjectsQuery();
  const settingsQuery = useSettingsQuery();
  const customFieldsQuery = useCustomFieldsQuery();
  const tasks = tasksQuery.data || EMPTY_ARRAY;
  const projects = projectsQuery.data || EMPTY_ARRAY;
  const loading =
    tasksQuery.isPending || projectsQuery.isPending || settingsQuery.isPending;
  const refetchTasks = useCallback(() => {
    qc.invalidateQueries({ queryKey: taskKeys.all });
    qc.invalidateQueries({ queryKey: projectKeys.all });
  }, [qc]);

  const { openTaskCreation } = useTask();
  const { openNew, openSearch } = useShellActions();
  // Tablets and up type into Home's composer; phones open New (the same input) from its card.
  const wide = useMediaQuery(SCREEN.md);
  const { data: mine } = useMyBotsQuery();
  // Home is my work; "Waiting on others" shows the tasks I gave others instead. The switch sits
  // above all the day cards and changes them all alike (the user, 2026-10-31: a switch inside
  // Today that also changed Overdue above it made no sense). Remembered per person here.
  const waitingKey = `bb.home.waiting.${user?.id}`;
  const [showWaiting, setShowWaiting] = useState(() => {
    try { return localStorage.getItem(waitingKey) === '1'; } catch { return false; }
  });
  const pickWaiting = (next) => {
    setShowWaiting(next);
    try { localStorage.setItem(waitingKey, next ? '1' : '0'); } catch { /* private mode */ }
  };
  // Every card (Overdue, Today, the next days) folds to its header; the folded ones are kept.
  const [folded, setFolded] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('bb.home.folded') || '[]')); } catch { return new Set(); }
  });
  const toggleFold = (id) => setFolded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    // Days gone by are forgotten.
    const today = new Date().toISOString().slice(0, 10);
    const kept = [...next].filter((x) => !x.startsWith('day:') || x.slice(4) >= today);
    try { localStorage.setItem('bb.home.folded', JSON.stringify(kept)); } catch { /* private mode */ }
    return new Set(kept);
  });
  const [customFieldDefs, setCustomFieldDefs] = useState([]);

  // Column visibility for timeline mode (shared across all day tables)
  const [visibleColumns, setVisibleColumns] = useState({
    title: true,
    status: true,
    priority: true,
    due_date: false,
    duration: true,
    project: false,
    labels: false,
    scheduled: false,
    task_type: false,
    assignee: true,
    created_at: false,
  });

  // Footer aggregation config for timeline mode
  const [footerConfig, setFooterConfig] = useState({
    title: { function: 'count' },
    status: { function: 'none' },
    priority: { function: 'none' },
    due_date: { function: 'none' },
    duration: { function: 'sum' },
    project: { function: 'none' },
    labels: { function: 'none' },
    scheduled: { function: 'none' },
    task_type: { function: 'none' },
    assignee: { function: 'none' },
    created_at: { function: 'none' },
  });

  const { toUserDateString, formatDate } = useDateTime();

  // Build dynamic column list: built-in + custom fields (custom default hidden in Agenda)
  const allColumnDefs = useMemo(() => [
    ...COLUMN_DEFINITIONS.map(col => ({ ...col, label: t(col.labelKey) })),
    ...customFieldDefs.map(def => ({
      key: `cf_${def.id}`,
      label: def.name,
      required: false,
      span: 2,
      defaultVisible: false,
      isCustom: true,
      fieldDef: def,
    })),
  ], [customFieldDefs, t]);

  // Apply settings + custom-field-derived UI state whenever the queries resolve.
  useEffect(() => {
    const settings = settingsQuery.data;
    const defs = customFieldsQuery.data || [];

    setCustomFieldDefs(defs);

    const tableConfig = settings?.table_config?.agenda;
    const cols = {};
    COLUMN_DEFINITIONS.forEach((col) => {
      if (tableConfig?.columns) {
        const saved = tableConfig.columns[col.key];
        cols[col.key] = saved != null ? saved.visible !== false : col.defaultVisible ?? true;
      } else {
        cols[col.key] = col.defaultVisible ?? true;
      }
    });
    defs.forEach((def) => {
      const key = `cf_${def.id}`;
      const saved = tableConfig?.columns?.[key];
      cols[key] = saved != null ? saved.visible !== false : false;
    });
    setVisibleColumns(cols);

    if (tableConfig?.footer) {
      setFooterConfig((prev) => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(tableConfig.footer).map(([key, val]) => [
            key,
            { function: val.function || 'none' },
          ])
        ),
      }));
    }
  }, [settingsQuery.data, customFieldsQuery.data]);

  // Toggle column visibility
  const handleToggleColumn = useCallback(async (columnKey) => {
    const newValue = !visibleColumns[columnKey];
    setVisibleColumns(prev => ({ ...prev, [columnKey]: newValue }));

    try {
      await settingsApi.update({
        [`table_config.agenda.columns.${columnKey}.visible`]: newValue
      });
    } catch (err) {
      console.error('Failed to save column visibility:', err);
    }
  }, [visibleColumns]);

  // Update footer aggregation config
  const handleFooterConfigChange = useCallback(async (columnKey, fn) => {
    setFooterConfig(prev => ({ ...prev, [columnKey]: { function: fn } }));

    try {
      await settingsApi.update({
        [`table_config.agenda.footer.${columnKey}`]: { function: fn }
      });
    } catch (err) {
      console.error('Failed to save footer config:', err);
    }
  }, []);

  // Optimistic update across every cached task list - the global axios
  // interceptor will reconcile from the server response shortly after.
  const handleOptimisticUpdate = useCallback(
    (taskId, updates) => {
      const lists = qc.getQueriesData({ queryKey: taskKeys.lists() });
      lists.forEach(([key, list]) => {
        if (!Array.isArray(list)) return;
        qc.setQueryData(
          key,
          list.map((t) => (t.id === taskId ? { ...t, ...updates } : t))
        );
      });
    },
    [qc]
  );

  // Home is a personal view: what is mine to do (assigned to me, or mine and given to
  // nobody). The day calendar and the greeting count only these.
  const visibleTasks = useMemo(
    () => (user?.id ? tasks.filter((t) => isMyWork(t, user.id)) : tasks),
    [tasks, user]
  );
  // The tasks I gave others.
  const delegatedTasks = useMemo(
    () => (user?.id ? tasks.filter((t) => isDelegatedByUser(t, user.id)) : []),
    [tasks, user]
  );
  const hasDelegated = delegatedTasks.length > 0;
  const waiting = showWaiting && hasDelegated;

  // The days on Home: Today and the next three working days (settings: working days and extra
  // work days), as Home always had them. Days are the person's (YYYY-MM-DD in their time zone).
  const todayKey = toUserDateString(new Date());
  // The same object all day, so the (memoized) table is not drawn again with the page.
  const todayStart = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, [todayKey]);
  const settings = settingsQuery.data;
  const nextDays = useMemo(() => {
    const workDays = settings?.working_hours?.days || [0, 1, 2, 3, 4];  // 0 = Monday
    const extra = settings?.extra_work_days || [];
    const out = [];
    for (let ahead = 1; ahead <= 14 && out.length < 3; ahead += 1) {
      const at = new Date(todayStart);
      at.setDate(at.getDate() + ahead);
      at.setHours(12, 0, 0, 0);
      const key = toUserDateString(at);
      const [y, m, d] = key.split('-').map(Number);
      const weekday = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
      if (workDays.includes(weekday) || extra.includes(key)) out.push({ key, date: at, ahead });
    }
    return out;
  }, [settings, todayStart, toUserDateString]);

  // Open tasks by day (planned, else due) in time order, then priority; and the overdue ones
  // (a day before today, due or planned).
  const buckets = useCallback((list) => {
    const plannedDay = (task) => (task.scheduled_start ? toUserDateString(new Date(task.scheduled_start)) : null);
    const dueDay = (task) => (task.due_date ? String(task.due_date).slice(0, 10) : null);
    const priorityOrder = { p1: 1, p2: 2, p3: 3, p4: 4 };
    const byDay = new Map();
    const overdue = [];
    list.forEach((task) => {
      if (task.status === 'completed' || task.completed_at) return;
      const due = dueDay(task);
      const planned = plannedDay(task);
      if ((due && due < todayKey) || (planned && planned < todayKey)) overdue.push(task);
      const day = planned || due;
      if (day && day >= todayKey) byDay.set(day, [...(byDay.get(day) || []), task]);
    });
    byDay.forEach((rows) => rows.sort((a, b) => {
      const timeA = a.scheduled_start ? new Date(a.scheduled_start).getTime() : Infinity;
      const timeB = b.scheduled_start ? new Date(b.scheduled_start).getTime() : Infinity;
      if (timeA !== timeB) return timeA - timeB;
      return (priorityOrder[a.priority] || 4) - (priorityOrder[b.priority] || 4);
    }));
    return { byDay, overdue };
  }, [todayKey, toUserDateString]);
  const { byDay, overdue: overdueTasks } = useMemo(
    () => buckets(waiting ? delegatedTasks : visibleTasks), [buckets, waiting, delegatedTasks, visibleTasks]);
  const todayTasks = byDay.get(todayKey) || EMPTY;
  const waitingCount = useMemo(() => {
    const { byDay: theirs, overdue } = buckets(delegatedTasks);
    return overdue.length + (theirs.get(todayKey) || []).length;
  }, [buckets, delegatedTasks, todayKey]);
  const dayTitle = ({ key, ahead }) => (ahead === 1 ? t('common.tomorrow')
    : new Intl.DateTimeFormat(i18n.language, { weekday: 'long', timeZone: 'UTC' })
      .format(new Date(`${key}T12:00:00Z`)).replace(/^./, (c) => c.toLocaleUpperCase(i18n.language)));

  if (loading) {
    return (
      <div className="space-y-4">
        <PageHeaderSkeleton />
        <TaskListSkeleton rows={6} />
      </div>
    );
  }

  const queen = mine?.queen || { id: 'queen', name: 'BusyBee', queen: true, owner_type: 'user' };
  const tableProps = {
    projects,
    onUpdate: refetchTasks,
    onOptimisticUpdate: handleOptimisticUpdate,
    showSearch: false,
    showFilters: false,
    showGrouping: false,
    showAddButton: false,
    showColumnSelector: false,
    defaultGroupBy: 'none',
    tableId: 'agenda',
    externalFooterConfig: footerConfig,
    onExternalFooterConfigChange: handleFooterConfigChange,
  };

  return (
    <div className="space-y-5 md:space-y-6" data-testid="home">
      {/* Desktop header: the day and the greeting (phones have them in the top bar) */}
      <div className="hidden md:flex items-end justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <p className="text-[13px] text-muted-foreground">{todayText(i18n.language)}</p>
          <h1 className="text-[24px] leading-tight font-semibold tracking-tight text-foreground truncate">
            {greetingText(t, user)}
          </h1>
        </div>
        <Button variant="outline" onClick={openSearch} className="gap-2 flex-shrink-0" data-testid="home-search">
          <Search className="w-4 h-4" />{t('nav.search')}
          <kbd className="font-mono text-[11px] font-normal text-muted-foreground">
            {/Mac|iPhone|iPad/.test(navigator.platform || '') ? '⌘/' : 'Ctrl /'}
          </kbd>
        </Button>
      </div>

      {/* The one input: a task, or a question for BusyBee (components/create/QuickInput.jsx) */}
      {wide ? <QuickInput variant="home" /> : (
        <button type="button" onClick={() => openNew()} data-testid="home-composer-card"
          className="w-full flex items-center gap-2.5 rounded-xl border border-border bg-card p-3 pl-2 text-left active:bg-accent/60 transition-colors">
          <BotAvatar bot={queen} size={32} />
          <span className="flex-1 min-w-0 truncate text-[15px] text-muted-foreground">{t('nav.create.placeholder')}</span>
          <Mic className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        </button>
      )}

      <HomeNeedsYou />

      {/* Zero-tasks "Get started" hero - shown only when no tasks exist */}
      {tasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-4">
            <CalendarCheck className="w-7 h-7 text-amber-700 dark:text-amber-400" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">{t('agenda.welcome_title')}</h2>
          <p className="text-muted-foreground text-sm mb-8 max-w-xs">
            {t('agenda.welcome_description')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-2xl mb-8">
            <div className="bg-accent/60 border border-border rounded-xl p-4 text-left">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center mb-3">
                <Plus className="w-4 h-4 text-amber-700 dark:text-amber-400" />
              </div>
              <p className="text-xs font-semibold text-foreground mb-1">{t('agenda.step_add_task_title')}</p>
              <p className="text-xs text-muted-foreground">{t('agenda.step_add_task_desc')}</p>
            </div>
            <div
              className="bg-accent/60 border border-border rounded-xl p-4 text-left cursor-pointer hover:border-input transition-colors"
              onClick={() => navigate('/app/settings?tab=working-hours')}
            >
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center mb-3">
                <Settings className="w-4 h-4 text-blue-400" />
              </div>
              <p className="text-xs font-semibold text-foreground mb-1">{t('agenda.step_working_hours_title')}</p>
              <p className="text-xs text-muted-foreground">{t('agenda.step_working_hours_desc')}</p>
            </div>
            <div
              className="bg-accent/60 border border-border rounded-xl p-4 text-left cursor-pointer hover:border-input transition-colors"
              onClick={() => navigate('/app/planner')}
            >
              <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center mb-3">
                <CalendarDays className="w-4 h-4 text-purple-400" />
              </div>
              <p className="text-xs font-semibold text-foreground mb-1">{t('agenda.step_planner_title')}</p>
              <p className="text-xs text-muted-foreground">{t('agenda.step_planner_desc')}</p>
            </div>
          </div>

          {/* The composer above is the page's one honey action: this one stays quiet. */}
          <Button variant="outline" onClick={() => openTaskCreation()}>
            <Plus className="w-4 h-4 mr-2" />
            {t('agenda.add_first_task')}
          </Button>
        </div>
      ) : (
        /* Overdue first, then Today and the next working days, all cards of one make with the same
           columns; above them the one switch and column choice for all (the user, 2026-10-31).
           Beside them, today in the Planner's calendar. */
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-x-6 gap-y-4 items-start">
          {/* A row of its own over the task cards, so the first card and Schedule start level. */}
          <div className={`${hasDelegated ? 'flex' : 'hidden md:flex'} items-center gap-3 min-h-8 min-w-0 xl:col-start-1 xl:row-start-1`} data-testid="home-tasks-bar">
            {hasDelegated && (
              <Segmented value={waiting ? 'waiting' : 'mine'} onChange={(key) => pickWaiting(key === 'waiting')}
                testId="home-waiting-toggle" items={[
                  { key: 'mine', label: t('tasks.my_tasks') },
                  { key: 'waiting', label: t('agenda.waiting_on_others'), count: waitingCount || null },
                ]} />
            )}
            <div className="ml-auto flex items-center gap-2">
              <div className="hidden md:block">
                <ColumnSelector columns={allColumnDefs} visibleColumns={visibleColumns} onToggleColumn={handleToggleColumn} />
              </div>
              <Link to="/app/tasks" className="text-xs text-muted-foreground hover:text-foreground">{t('agenda.all_tasks')}</Link>
            </div>
          </div>
          <div className="space-y-4 min-w-0 xl:col-start-1 xl:row-start-2">
          {overdueTasks.length > 0 && (
            <FoldCard id="overdue" title={t('agenda.overdue')} count={overdueTasks.length} countClass="text-red-500 dark:text-red-400"
              open={!folded.has('overdue')} onToggle={toggleFold}>
              {/* No quick-add: a new "overdue" task makes no sense. */}
              <TasksTable {...tableProps} frameless tasks={overdueTasks} showInlineAdd={false} externalVisibleColumns={visibleColumns} />
            </FoldCard>
          )}
          <FoldCard id="today" title={t('common.today')} count={todayTasks.length} meta={formatDate(todayStart)}
            open={!folded.has('today')} onToggle={toggleFold}>
            {todayTasks.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <Calendar className="w-6 h-6 mx-auto mb-2 opacity-60" />
                <p className="text-sm font-medium mb-3 text-muted-foreground/70 dark:text-foreground/80">{t('agenda.no_tasks_scheduled')}</p>
                {!waiting && (
                  <button
                    onClick={() => { openTaskCreation({ date: todayStart }); }}
                    className="text-xs text-muted-foreground dark:text-muted-foreground hover:text-amber-500 dark:hover:text-amber-400 transition-colors flex items-center gap-1 mx-auto font-medium"
                  >
                    <Plus className="w-3 h-3" /> {t('agenda.add_task_for_day')}
                  </button>
                )}
              </div>
            ) : (
              <TasksTable {...tableProps} frameless tasks={todayTasks} prefillDueDate={todayStart} showInlineAdd={!waiting}
                externalVisibleColumns={visibleColumns} />
            )}
          </FoldCard>
          {/* The next working days, each when something is planned or due then. */}
          {nextDays.map((day) => {
            const rows = byDay.get(day.key);
            if (!rows?.length) return null;
            return (
              <FoldCard key={day.key} id={`day:${day.key}`} title={dayTitle(day)} count={rows.length} meta={formatDate(day.date)}
                open={!folded.has(`day:${day.key}`)} onToggle={toggleFold}>
                <TasksTable {...tableProps} frameless tasks={rows} prefillDueDate={day.date} showInlineAdd={!waiting}
                  externalVisibleColumns={visibleColumns} />
              </FoldCard>
            );
          })}
          </div>

          {wide && (
            <section className="rounded-xl border border-border bg-accent/60 min-w-0 overflow-hidden xl:col-start-2 xl:row-start-2" data-testid="home-schedule">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <h2 className="text-sm font-semibold text-foreground">{t('agenda.schedule_title')}</h2>
                <Link to="/app/planner" className="text-xs text-muted-foreground hover:text-foreground">{t('agenda.open_calendar')}</Link>
              </div>
              <HomeDayCalendar tasks={visibleTasks} projects={projects} />
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default AgendaPage;
